<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-lg-8">
        <div class="page-bar py-3">
          <div class="d-flex align-items-center">
            <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
              <i class="fas fa-chevron-left"></i>
            </BackButton>
            <div class="page-title flex-grow-1">Event Settings</div>
          </div>
        </div>

        <div class="card shadow-sm">
          <div class="card-body settings-soft-panel">
            <!-- RSVP -->
            <div class="section-block settings-soft-panel">
              <div class="section-head">
                <div class="section-title mb-1">RSVP & Joining</div>
                <p class="section-sub mb-0">Decide how people can participate</p>
              </div>

              <div class="settings-tile">
                <div>
                  <div class="settings-tile-title">Accept RSVP</div>
                  <div class="settings-tile-sub">Guests can RSVP (Yes / No / Maybe) if enabled.</div>
                </div>
                <label class="switch mb-0">
                  <input type="checkbox" v-model="form.acceptRsvp" />
                  <span class="slider round"></span>
                </label>
              </div>

              <div class="settings-tile" :class="{ 'tile-disabled': !form.acceptRsvp }">
                <div>
                  <div class="settings-tile-title">Guest Count (Adults/Children)</div>
                  <div class="settings-tile-sub">Let guests set how many are coming.</div>
                </div>
                <label class="switch mb-0">
                  <input type="checkbox" v-model="form.guestCount" :disabled="!form.acceptRsvp" />
                  <span class="slider round"></span>
                </label>
              </div>

              <div class="settings-tile">
                <div>
                  <div class="settings-tile-title">Enable Check-In</div>
                  <div class="settings-tile-sub">Let guests check in when they arrive. Hosts can track attendance in real time.</div>
                </div>
                <label class="switch mb-0">
                  <input type="checkbox" v-model="form.checkinEnabled" />
                  <span class="slider round"></span>
                </label>
              </div>
            </div>

            <!-- Album -->
            <div class="section-block settings-soft-panel">
              <div class="section-head">
                <div class="section-title mb-1">Album</div>
                <p class="section-sub mb-0">Enable album to share event photos</p>
              </div>

              <div class="settings-tile">
                <div>
                  <div class="settings-tile-title">Album</div>
                  <div class="settings-tile-sub">
                    Capture and share memories effortlessly with our photo booth module, offering secure and customizable image and video sharing post-event.
                  </div>
                </div>
                <label class="switch mb-0">
                  <input type="checkbox" v-model="form.albumEnabled" />
                  <span class="slider round"></span>
                </label>
              </div>
            </div>

            <!-- Feedback -->
            <div class="section-block settings-soft-panel">
              <div class="section-head">
                <div class="section-title mb-1">Feedback</div>
                <p class="section-sub mb-0">Let guests share how it went</p>
              </div>

              <div class="settings-tile">
                <div>
                  <div class="settings-tile-title">Enable Feedback</div>
                  <div class="settings-tile-sub">
                    Collect private feedback from attendees to learn what they enjoyed and what could be improved for future events.
                  </div>
                </div>
                <label class="switch mb-0">
                  <input type="checkbox" v-model="form.feedbackEnabled" />
                  <span class="slider round"></span>
                </label>
              </div>

              <div class="settings-tile" :class="{ 'tile-disabled': !form.feedbackEnabled }">
                <div>
                  <div class="settings-tile-title">Enable Rating</div>
                  <div class="settings-tile-sub">Let guests rate the event along with feedback.</div>
                </div>
                <label class="switch mb-0">
                  <input type="checkbox" v-model="form.ratingEnabled" :disabled="!form.feedbackEnabled" />
                  <span class="slider round"></span>
                </label>
              </div>
            </div>

            <AttachmentOptions v-model="attachments" class="mb-3" />
          </div>

          <div class="card-footer d-flex justify-content-end gap-2">
            <button class="btn btn-light" :disabled="saving" @click="goBack">Cancel</button>
            <button class="btn btn-primary" :disabled="saving" @click="saveSettings">
              <span v-if="saving" class="spinner-border spinner-border-sm mr-2"></span>
              {{ saving ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </div>

        <div v-if="loadError" class="alert alert-danger mt-3">
          {{ loadError }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import api from '@/services/api'
import BackButton from '@/components/common/BackButton.vue'
import AttachmentOptions from '@/components/common/AttachmentOptions.vue'
import { useSessionStore } from '@/stores/session'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()

const eventId = computed(() => route.params?.slug || route.params?.eventid || '')

const form = ref({
  acceptRsvp: true,
  guestCount: false,
  checkinEnabled: true,
  albumEnabled: true,
  feedbackEnabled: false,
  ratingEnabled: false
})

const attachments = ref({
  textPhoto: false,
  schedule: false,
  poll: false,
  carPool: false
})
const saving = ref(false)
const loadError = ref('')

const normalizeFlag = (val) => {
  const s = String(val ?? '').trim().toLowerCase()
  if (s === '1' || s === 'yes' || s === 'true') return true
  return Number(s) === 1
}
const toFlag = (boolVal) => (boolVal ? '1' : '0')
const toYesNo = (boolVal) => (boolVal ? 'yes' : 'no')
const moduleKeyMap = {
  text_photo: 'textPhoto',
  schedule: 'schedule',
  poll: 'poll',
  car_pool: 'carPool'
}

const loadDetail = async () => {
  if (!eventId.value) return
  loadError.value = ''
  try {
    const fd = new FormData()
    fd.append('eventid', eventId.value)
    fd.append('userid', session.userId || '')
    const res = await api.getInvitationDetail(fd)
    const ok = res?.data?.status === 'ok'
    const info = ok ? (res.data?.eventsinfo?.[0] || {}) : {}
    form.value.acceptRsvp = normalizeFlag(info.rsvp_option ?? info.rsvpOption ?? 1)
    form.value.guestCount = form.value.acceptRsvp && normalizeFlag(info.rsvp_family ?? info.rsvpFamily ?? 1)
    form.value.checkinEnabled = normalizeFlag(info.checkin_option ?? info.checkinOption ?? 1)
    form.value.albumEnabled = normalizeFlag(info.photo_booth ?? info.album ?? info.album_enabled ?? info.albumEnabled ?? 1)
    form.value.feedbackEnabled = normalizeFlag(info.enable_feedback ?? info.feedback_enabled ?? info.feedback)
    form.value.ratingEnabled = normalizeFlag(info.enable_rating ?? info.rating_enabled ?? info.rating)
  } catch (e) {
    console.error('load settings failed', e)
    loadError.value = 'Unable to load event settings right now.'
  }
}

const loadInviteModules = async () => {
  if (!eventId.value) return
  try {
    const fd = new FormData()
    fd.append('event_id', eventId.value)
    const res = await api.getInviteModules(fd)
    const payload = res?.data ?? res
    const list = Array.isArray(payload?.data) ? payload.data : []
    const next = {
      textPhoto: false,
      schedule: false,
      poll: false,
      carPool: false
    }
    list.forEach((item) => {
      const rawKey = String(item?.value || item?.id || '').trim().toLowerCase()
      const mappedKey = moduleKeyMap[rawKey]
      if (!mappedKey) return
      next[mappedKey] = normalizeFlag(item?.status)
    })
    attachments.value = next
  } catch (e) {
    console.error('load invite modules failed', e)
  }
}

const saveSettings = async () => {
  if (!eventId.value) return
  try {
    if (!form.value.feedbackEnabled) form.value.ratingEnabled = false
    saving.value = true
    const fd = new FormData()
    fd.append('eventid', eventId.value)
    fd.append('rsvp_option', toFlag(form.value.acceptRsvp))
    fd.append('rsvp_family', toFlag(form.value.guestCount))
    fd.append('checkin_option', toFlag(form.value.checkinEnabled))
    fd.append('photo_booth', toYesNo(form.value.albumEnabled))
    fd.append('feedback', toFlag(form.value.feedbackEnabled))
    fd.append('rating', toFlag(form.value.ratingEnabled && form.value.feedbackEnabled))
    fd.append('car_pool', attachments.value.carPool ? '1' : '0')
    fd.append('poll', attachments.value.poll ? '1' : '0')
    fd.append('schedule', attachments.value.schedule ? '1' : '0')
    fd.append('text_photo', attachments.value.textPhoto ? '1' : '0')

    const res = await api.updateEventSettings(fd)
    const ok = res?.data?.status === 'ok' || res?.data?.status === true || res?.status === true
    if (!ok) throw new Error(res?.data?.msg || 'Save failed')

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Settings updated',
      showConfirmButton: false,
      timer: 1800,
      timerProgressBar: true
    })
    router.back()
  } catch (e) {
    console.error('save settings failed', e)
    Swal.fire({
      icon: 'error',
      title: 'Could not save settings',
      text: e?.message || 'Please try again.'
    })
  } finally {
    saving.value = false
  }
}

const goBack = () => router.back()

onMounted(() => {
  session.loadUser?.()
  loadDetail()
  loadInviteModules()
})

watch(
  () => form.value.acceptRsvp,
  (val) => {
    if (!val) form.value.guestCount = false
  }
)
</script>

<style scoped>
.page-title{font-weight:700;font-size:1.2rem;}
.card{border-radius:14px;border:1px solid #f3e8ee;background:#fff;box-shadow:0 12px 26px rgba(15,23,42,.06);}
.card-footer{background:#f1f4fa;}
.section-title{font-weight:700;color:#0f172a;font-size:1rem;line-height:1.3;}
.section-sub{color:#6b7280;font-size:.9rem;line-height:1.4;margin:0 0 10px;}
.section-head{margin-bottom:10px;}
.section-block{
  padding:16px;
  margin-bottom:16px;
  border-radius:14px;
  border:1px solid #f3e8ee;
  background:linear-gradient(180deg,#fff7f8 0%, #ffffff 70%);
  box-shadow:0 12px 26px rgba(15,23,42,.06);
}
.section-block:last-child{margin-bottom:0;}
.settings-tile{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  padding:12px 14px;
  border-radius:14px;
  background:#fff;
  border:1px solid #f1f5f9;
  margin-bottom:12px;
}
.settings-tile:last-child{margin-bottom:0;}
.settings-tile-title{font-weight:700;color:#0f172a;font-size:1rem;line-height:1.3;}
.settings-tile-sub{color:#64748b;font-size:.9rem;line-height:1.4;margin-bottom:0;}
.settings-tile .switch{flex-shrink:0;}
.switch{position:relative;display:inline-flex;align-items:center;justify-content:center;width:46px;height:26px;cursor:pointer;}
.switch input{position:absolute;opacity:0;width:0;height:0;}
.slider{
  position:absolute;
  top:0;left:0;right:0;bottom:0;
  background:#e2e8f0;
  transition:.2s;
  border-radius:999px;
  box-shadow:none;
}
.slider:before{
  position:absolute;content:"";
  height:20px;width:20px;
  left:3px;bottom:3px;
  background:#fff;
  transition:.2s;
  border-radius:50%;
  box-shadow:0 4px 10px rgba(15,23,42,.15);
}
input:checked + .slider{background:#f472b6;box-shadow:none;}
input:checked + .slider:before{transform:translateX(20px);}
.switch input:focus-visible + .slider{
  outline:2px solid #f9a8d4;
  outline-offset:2px;
}
.slider.round{border-radius:999px;}

.settings-tile.tile-disabled{opacity:.6;pointer-events:none;}
.settings-tile.tile-disabled .slider{background:#e5eaf1;box-shadow:none;}

@media (max-width: 576px){
  .settings-tile{
    flex-direction:column;
    align-items:flex-start;
  }
  .settings-tile .switch{
    align-self:flex-end;
    margin-top:.35rem;
  }
}
</style>
